import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { iotDeviceEvents } from "../../data/iotDeviceEvents.data";
import { formatDateTime, getUniqueItems } from "../../utils";
import { IotDeviceEventLatestDto } from "./dtos/IotDeviceEventLatestDto";
import { useIotDeviceEvents } from "./useIotDeviceEvents";
import type {} from '@mui/x-data-grid/themeAugmentation';
import { DashboardEventsLatest } from "../../components/DashboardEventsLatest";
import { IotDeviceEventDto } from "./dtos/IotDeviceEventDto";
import { IotDeviceEvents } from "./IotDeviceEvents";
import { Dashboard } from "../../enums/Dashboard";


type DataRow = {

    id: string;
    deviceEuid : string;
    gatewayEuid : string;
    status : string;
    rssi : string;
    lqi : number;
    deviceName : string;
    lastSeenDateTime : Date;
    networkMac : string;

};



const getColumns = ( rows: DataRow[] ): GridColDef[ ] => {

    // do not display status column if only one status was selected
    const distinctStatuses = getUniqueItems( rows.map( row => row.status ) );

    let columns: GridColDef[ ] = [ ];

    if( distinctStatuses.length === 1) {

        columns = [

            {
                field: 'deviceEuid',
                headerName: 'Device Euid',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'gatewayEuid',
                headerName: 'Gateway Euid',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'rssi',
                headerName: 'RSSI',
                width: 100,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'lqi',
                headerName: 'LQI',
                width: 100,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'deviceName',
                headerName: 'Device Name',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'lastSeenDateTime',
                headerName: 'Last Seen',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center',
                renderCell: ( params => formatDateTime( params.row.lastSeenDateTime ))
            },

            {
                field: 'networkMac',
                headerName: 'Network Mac',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

        ];

    } else {

        columns = [

            {
                field: 'deviceEuid',
                headerName: 'Device Euid',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'gatewayEuid',
                headerName: 'Gateway Euid',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'rssi',
                headerName: 'RSSI',
                width: 100,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'lqi',
                headerName: 'LQI',
                width: 100,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'deviceName',
                headerName: 'Device Name',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'lastSeenDateTime',
                headerName: 'Last Seen',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center',
                renderCell: ( params => formatDateTime( params.row.lastSeenDateTime ))
            },

            {
                field: 'networkMac',
                headerName: 'Network Mac',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

            {
                field: 'status',
                headerName: 'Status',
                width: 200,
                editable: true,
                headerAlign: 'center',
                align: 'center'
            },

        ];

    }

    return columns

};


const getRows = ( iotDeviceEventsLatest: IotDeviceEventLatestDto[] ) : DataRow[] => {

    const rows: DataRow[] = [];

    for( const iotDeviceEventLatest of iotDeviceEventsLatest ) {

        const iotDeviceEvent = iotDeviceEvents.find( be => be.event === iotDeviceEventLatest.status );

        if( !iotDeviceEvent ) {
            
            throw Error( `Iot Device status ${iotDeviceEventLatest.status} not found` );

        }
       
        rows.push({ 
            id: iotDeviceEventLatest.deviceEuid + iotDeviceEventLatest.lastSeenDateTime,
            deviceEuid : iotDeviceEventLatest.deviceEuid,
            gatewayEuid : iotDeviceEventLatest.gatewayEuid,
            status : iotDeviceEventLatest.status,
            rssi : iotDeviceEventLatest.rssi,
            lqi : iotDeviceEventLatest.lqi,
            deviceName : iotDeviceEventLatest.deviceName,
            lastSeenDateTime : iotDeviceEventLatest.lastSeenDateTime,
            networkMac : iotDeviceEventLatest.networkMac
        });

    }

    return rows;

};


type IotDeviceEventsLatestProps = {

    events: string[];
    refreshCount: number;
    villageCode: string;

};


export const IotDeviceEventsLatest : React.FC< IotDeviceEventsLatestProps > = ( props : IotDeviceEventsLatestProps ) => {

    const { events, refreshCount, villageCode  } = props;

    const [ areIotDeviceEventsLatestLoading, setAreIotDeviceEventsLatestLoading ] = useState<boolean>( false );
    const [ iotDeviceEventDtos, setIotDeviceEventDtos ] = useState<IotDeviceEventDto[]>( [] );
    const [ columns, setColumns ] = useState< GridColDef[ ] >( [ ] );
    const [ displayIotDeviceEvents, setDisplayIotDeviceEvents ] = useState( false );
    const [ deviceName, setDeviceName ] = useState<string>( '' );
    const [ isChildLoading, setIsChildLoading ] = useState( false );
    const [ rows, setRows ] = useState<DataRow[ ]>( [ ] );

    const style = {

        width: ( columns.length === 7 ) ? 1250 : 1400

    };

    
    const { getIotDeviceEvents, getIotDeviceEventsLatest } = useIotDeviceEvents( );

    useEffect( ( ) => {

        if( events.length === 0 || !villageCode ) {

            setRows( [] );
            return;

        }

        const fetchIotDeviceEventsLatest = async( ) => {

            if( refreshCount === 0 ) {
            
                setAreIotDeviceEventsLatestLoading( true );

            }

            const batteryEventsLatest = await getIotDeviceEventsLatest( villageCode, events );

            if( refreshCount === 0 ) {
                
                setAreIotDeviceEventsLatestLoading( false );

            }

            if( batteryEventsLatest ) {

                const rows = getRows( batteryEventsLatest );
                
                setRows( rows );

                const columns = getColumns( rows );
                setColumns( columns );

            }

        };

        fetchIotDeviceEventsLatest( );

    }, [ events, refreshCount, villageCode ]);


    const selectRowHandler = async ( rowId: string ) => {

        const row = rows.find( r => r.id === rowId );

        if( !row  ) {

            throw Error( `Cannot find row with id = ${rowId}` );

        }
    
        setIsChildLoading( true );
        const iotDeviceEventDtos = await getIotDeviceEvents( row.deviceEuid, row.networkMac, villageCode );
        setIsChildLoading( false );

        if( !iotDeviceEventDtos ) {

            return;

        }

        setIotDeviceEventDtos( iotDeviceEventDtos );
        setDeviceName( row.deviceName );
        setDisplayIotDeviceEvents( true );

    };


    const handleClose = ( ) => setDisplayIotDeviceEvents( false );

    

    return(

        <>

            <DashboardEventsLatest 
                
                columns={ columns } 
                dashboard={ Dashboard.IotDevice }
                isChildLoading={ isChildLoading } 
                isParentLoading={ areIotDeviceEventsLatestLoading } 
                rows={ rows }
                selectRow={ selectRowHandler }
                sortField="lastSeenDateTime"
                style={ style } 

            />

            <IotDeviceEvents displayIotDeviceEvents={displayIotDeviceEvents} deviceName={deviceName} onClose={handleClose} iotDeviceEventDtos={iotDeviceEventDtos} />

        </>

    );


};